import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { HoursEditor } from "@/components/HoursEditor";
import { Plus, Save, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#C5A059", "#7C9CBF", "#A07CBF", "#7CBF8E", "#BF7C7C", "#BFA77C", "#7CBFB8", "#BF7CA8"];
const WEEKDAYS = [
  ["all", "Tous les jours"], ["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"],
  ["thursday", "Jeudi"], ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"],
];
const WEEKDAY_LABEL = Object.fromEntries(WEEKDAYS);

export function StaffManager({ staff, setStaff }) {
  const [newStaff, setNewStaff] = useState({ name: "", title: "", color: COLORS[0], photo_url: "" });
  const [expanded, setExpanded] = useState(null);
  const [breaks, setBreaks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/owner/breaks");
      if (!cancelled) setBreaks(data);
    })();
    return () => { cancelled = true; };
  }, []);

  const addBreak = async (payload) => {
    try {
      const { data } = await api.post("/owner/breaks", payload);
      setBreaks((b) => [...b, data]);
      toast.success("Pause / absence ajoutée");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteBreak = async (id) => {
    try {
      await api.delete(`/owner/breaks/${id}`);
      setBreaks((b) => b.filter((x) => x.id !== id));
      toast.success("Supprimée");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/owner/staff", newStaff);
      setStaff([...staff, data]);
      setNewStaff({ name: "", title: "", color: COLORS[0], photo_url: "" });
      toast.success("Coiffeur ajouté");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/owner/staff/${id}`, patch);
      setStaff(staff.map((s) => (s.id === id ? data : s)));
      toast.success("Coiffeur mis à jour");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce coiffeur ?")) return;
    try {
      await api.delete(`/owner/staff/${id}`);
      setStaff(staff.filter((s) => s.id !== id));
      toast.success("Coiffeur supprimé");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="settings-staff">
      <h2 className="font-serif text-3xl font-light mb-3">Coiffeurs / Collaborateurs</h2>
      <p className="text-sm text-[#A39E93] mb-8">Ajoutez vos coiffeurs et définissez leurs horaires individuels. Les clients pourront les choisir lors de la réservation.</p>

      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-8 p-6 bg-[#141311] border border-[#2C2A26]" data-testid="staff-add-form">
        <Input placeholder="Nom" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} required className="md:col-span-3 bg-[#0A0908] border-[#2C2A26]" data-testid="staff-new-name" />
        <Input placeholder="Rôle (ex: Barbier)" value={newStaff.title} onChange={(e) => setNewStaff({ ...newStaff, title: e.target.value })} className="md:col-span-3 bg-[#0A0908] border-[#2C2A26]" data-testid="staff-new-title" />
        <Input placeholder="URL photo (optionnel)" value={newStaff.photo_url} onChange={(e) => setNewStaff({ ...newStaff, photo_url: e.target.value })} className="md:col-span-4 bg-[#0A0908] border-[#2C2A26]" data-testid="staff-new-photo" />
        <button type="submit" className="btn-primary justify-center md:col-span-2" data-testid="staff-add-btn"><Plus className="w-4 h-4" /> Ajouter</button>
        <div className="md:col-span-12 flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-[0.2em] text-[#A39E93] mr-2">Couleur agenda</span>
          {COLORS.map((c) => (
            <button type="button" key={c} onClick={() => setNewStaff({ ...newStaff, color: c })} className={`w-7 h-7 rounded-full border-2 transition ${newStaff.color === c ? "border-white" : "border-transparent"}`} style={{ backgroundColor: c }} data-testid={`staff-new-color-${c}`} aria-label={`Couleur ${c}`} />
          ))}
        </div>
      </form>

      {staff.length === 0 ? (
        <p className="text-[#6B6760] text-sm">Aucun coiffeur. Ajoutez-en un ci-dessus.</p>
      ) : (
        <div className="space-y-4">
          {staff.map((s) => (
            <StaffRow key={s.id} staff={s} expanded={expanded === s.id} onToggle={() => setExpanded(expanded === s.id ? null : s.id)} onUpdate={(patch) => update(s.id, patch)} onRemove={() => remove(s.id)} breaks={breaks.filter((b) => b.staff_id === s.id)} onAddBreak={addBreak} onDeleteBreak={deleteBreak} />
          ))}
        </div>
      )}
    </section>
  );
}

function StaffRow({ staff, expanded, onToggle, onUpdate, onRemove, breaks, onAddBreak, onDeleteBreak }) {
  const [info, setInfo] = useState({ name: staff.name, title: staff.title || "", color: staff.color || COLORS[0], photo_url: staff.photo_url || "" });
  const dirty = info.name !== staff.name || info.title !== (staff.title || "") || info.color !== (staff.color || COLORS[0]) || info.photo_url !== (staff.photo_url || "");
  return (
    <div className="bg-[#141311] border border-[#2C2A26]" data-testid={`staff-row-${staff.id}`}>
      <div className="flex items-center justify-between p-5 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 flex items-center justify-center bg-[#0A0908] shrink-0" style={{ borderColor: staff.color }}>
            {staff.photo_url ? <img src={staff.photo_url} alt={staff.name} className="w-full h-full object-cover" /> : <span className="font-serif text-lg" style={{ color: staff.color }}>{staff.name.charAt(0)}</span>}
          </div>
          <div>
            <div className="font-serif text-xl">{staff.name}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6760]">{staff.title || "—"}{!staff.active && " · Inactif"}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#A39E93]">{staff.active ? "Actif" : "Inactif"}</span>
            <Switch checked={staff.active} onCheckedChange={(v) => onUpdate({ active: v })} data-testid={`staff-active-${staff.id}`} />
          </div>
          <button onClick={onToggle} className="text-[#C5A059] text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1" data-testid={`staff-expand-${staff.id}`}>
            {expanded ? <>Fermer <ChevronUp className="w-4 h-4" /></> : <>Modifier <ChevronDown className="w-4 h-4" /></>}
          </button>
          <button onClick={onRemove} className="text-[#A39E93] hover:text-[#8C3131]" data-testid={`staff-delete-${staff.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-[#2C2A26] p-5 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Nom</Label><Input value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid={`staff-edit-name-${staff.id}`} /></div>
            <div><Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Rôle</Label><Input value={info.title} onChange={(e) => setInfo({ ...info, title: e.target.value })} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid={`staff-edit-title-${staff.id}`} /></div>
            <div className="md:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">URL photo</Label><Input value={info.photo_url} onChange={(e) => setInfo({ ...info, photo_url: e.target.value })} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid={`staff-edit-photo-${staff.id}`} /></div>
            <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-[0.2em] text-[#A39E93] mr-2">Couleur</span>
              {COLORS.map((c) => <button type="button" key={c} onClick={() => setInfo({ ...info, color: c })} className={`w-7 h-7 rounded-full border-2 transition ${info.color === c ? "border-white" : "border-transparent"}`} style={{ backgroundColor: c }} aria-label={`Couleur ${c}`} />)}
            </div>
          </div>
          <button disabled={!dirty} onClick={() => onUpdate(info)} className="btn-primary disabled:opacity-30" data-testid={`staff-save-info-${staff.id}`}><Save className="w-4 h-4" /> Enregistrer les infos</button>

          <div>
            <h4 className="font-serif text-xl font-light mb-4">Horaires de {staff.name}</h4>
            <HoursEditor hours={staff.hours} onSave={(hours) => onUpdate({ hours })} testIdPrefix={`staff-hours-${staff.id}`} />
          </div>

          <BreaksEditor staffId={staff.id} breaks={breaks} onAdd={onAddBreak} onDelete={onDeleteBreak} />
        </div>
      )}
    </div>
  );
}

function BreaksEditor({ staffId, breaks, onAdd, onDelete }) {
  const [form, setForm] = useState({ type: "recurring", title: "", weekday: "all", date_start: "", date_end: "", all_day: false, time_start: "12:00", time_end: "14:00" });
  const submit = async (e) => {
    e.preventDefault();
    const payload = { staff_id: staffId, type: form.type, title: form.title };
    if (form.type === "recurring") {
      payload.weekday = form.weekday;
      payload.time_start = form.time_start;
      payload.time_end = form.time_end;
    } else {
      payload.date_start = form.date_start;
      payload.date_end = form.date_end || form.date_start;
      payload.all_day = form.all_day;
      if (!form.all_day) { payload.time_start = form.time_start; payload.time_end = form.time_end; }
    }
    await onAdd(payload);
  };
  const describe = (b) => {
    if (b.type === "recurring") return `${WEEKDAY_LABEL[b.weekday] || b.weekday} · ${b.time_start}–${b.time_end}`;
    const range = b.date_end && b.date_end !== b.date_start ? `${b.date_start} → ${b.date_end}` : b.date_start;
    return `${range} · ${b.all_day ? "Toute la journée" : `${b.time_start}–${b.time_end}`}`;
  };
  return (
    <div data-testid={`breaks-editor-${staffId}`}>
      <h4 className="font-serif text-xl font-light mb-4">Pauses & absences</h4>
      {breaks.length === 0 ? (
        <p className="text-[#6B6760] text-sm mb-4">Aucune pause ni absence programmée.</p>
      ) : (
        <div className="divide-y divide-[#2C2A26] border-y border-[#2C2A26] mb-4">
          {breaks.map((b) => (
            <div key={b.id} className="flex items-center justify-between py-3" data-testid={`break-row-${b.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-[#2C2A26] text-[#C5A059]">{b.type === "recurring" ? "Récurrent" : "Absence"}</span>
                <span className="text-sm">{b.title ? `${b.title} — ` : ""}{describe(b)}</span>
              </div>
              <button onClick={() => onDelete(b.id)} className="text-[#A39E93] hover:text-[#8C3131]" data-testid={`break-delete-${b.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="p-4 bg-[#0A0908] border border-[#2C2A26] space-y-3" data-testid={`break-form-${staffId}`}>
        <div className="flex gap-2">
          <button type="button" onClick={() => setForm({ ...form, type: "recurring" })} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${form.type === "recurring" ? "bg-[#C5A059] text-[#0A0908] border-[#C5A059]" : "border-[#2C2A26] text-[#A39E93]"}`} data-testid={`break-type-recurring-${staffId}`}>Pause récurrente</button>
          <button type="button" onClick={() => setForm({ ...form, type: "oneoff" })} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${form.type === "oneoff" ? "bg-[#C5A059] text-[#0A0908] border-[#C5A059]" : "border-[#2C2A26] text-[#A39E93]"}`} data-testid={`break-type-oneoff-${staffId}`}>Absence ponctuelle</button>
        </div>
        <Input placeholder="Titre (ex: Pause déjeuner)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#141311] border-[#2C2A26]" data-testid={`break-title-${staffId}`} />
        {form.type === "recurring" ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: e.target.value })} className="bg-[#141311] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid={`break-weekday-${staffId}`}>
              {WEEKDAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Input type="time" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} className="bg-[#141311] border-[#2C2A26]" data-testid={`break-time-start-${staffId}`} />
            <Input type="time" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} className="bg-[#141311] border-[#2C2A26]" data-testid={`break-time-end-${staffId}`} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">Du</Label><Input type="date" value={form.date_start} onChange={(e) => setForm({ ...form, date_start: e.target.value })} required className="mt-1 bg-[#141311] border-[#2C2A26]" data-testid={`break-date-start-${staffId}`} /></div>
              <div><Label className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">Au (optionnel)</Label><Input type="date" value={form.date_end} onChange={(e) => setForm({ ...form, date_end: e.target.value })} className="mt-1 bg-[#141311] border-[#2C2A26]" data-testid={`break-date-end-${staffId}`} /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })} data-testid={`break-allday-${staffId}`} />
              <span className="text-sm text-[#A39E93]">Toute la journée</span>
            </div>
            {!form.all_day && (
              <div className="grid grid-cols-2 gap-3">
                <Input type="time" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} className="bg-[#141311] border-[#2C2A26]" data-testid={`break-otime-start-${staffId}`} />
                <Input type="time" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} className="bg-[#141311] border-[#2C2A26]" data-testid={`break-otime-end-${staffId}`} />
              </div>
            )}
          </div>
        )}
        <button type="submit" className="btn-primary" data-testid={`break-add-${staffId}`}><Plus className="w-4 h-4" /> Ajouter</button>
      </form>
    </div>
  );
}
